/**
 * Preset morph transitions — vizz.fm-parity feature.
 *
 * Smoothly interpolates renderer.params from the current snapshot toward a
 * target preset's params, applied each frame in main.ts via tick(dt).
 * Supports 9 easing curves matching vizz's dropdown.
 */

import { BioclastParams, DEFAULT_BIOCLAST } from "../viz/scenes/bioclast";

export type EasingName =
  | "linear"
  | "ease-in"
  | "ease-out"
  | "ease-in-out"
  | "ease-in-cubic"
  | "ease-out-cubic"
  | "ease-in-out-cubic"
  | "ease-out-back"
  | "ease-out-elastic";

export const EASINGS: { value: EasingName; label: string }[] = [
  { value: "linear", label: "linear" },
  { value: "ease-in", label: "ease-in" },
  { value: "ease-out", label: "ease-out" },
  { value: "ease-in-out", label: "ease-in-out" },
  { value: "ease-in-cubic", label: "ease-in-cubic" },
  { value: "ease-out-cubic", label: "ease-out-cubic" },
  { value: "ease-in-out-cubic", label: "ease-in-out-cubic" },
  { value: "ease-out-back", label: "ease-out-back" },
  { value: "ease-out-elastic", label: "ease-out-elastic" },
];

export function ease(name: EasingName, t: number): number {
  const x = Math.max(0, Math.min(1, t));
  switch (name) {
    case "linear": return x;
    case "ease-in": return x * x;
    case "ease-out": return 1 - (1 - x) * (1 - x);
    case "ease-in-out": return x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;
    case "ease-in-cubic": return x * x * x;
    case "ease-out-cubic": return 1 - Math.pow(1 - x, 3);
    case "ease-in-out-cubic": return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
    case "ease-out-back": {
      const c1 = 1.70158;
      const c3 = c1 + 1;
      return 1 + c3 * Math.pow(x - 1, 3) + c1 * Math.pow(x - 1, 2);
    }
    case "ease-out-elastic": {
      const c4 = (2 * Math.PI) / 3;
      if (x === 0) return 0;
      if (x === 1) return 1;
      return Math.pow(2, -10 * x) * Math.sin((x * 10 - 0.75) * c4) + 1;
    }
  }
}

export interface MorphState {
  active: boolean;
  startedAt: number;
  duration: number;
  easing: EasingName;
  /** Snapshot of params at morph start. */
  from: Partial<BioclastParams>;
  /** Target params to lerp toward. */
  to: Partial<BioclastParams>;
}

export function createMorphState(): MorphState {
  return { active: false, startedAt: 0, duration: 1, easing: "ease-in-out", from: {}, to: {} };
}

/** Numeric keys eligible for lerping (booleans/strings snap at 50% threshold). */
export function snapshotParams(p: BioclastParams): Partial<BioclastParams> {
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(p)) {
    out[k] = (p as unknown as Record<string, unknown>)[k];
  }
  return out as Partial<BioclastParams>;
}

export function startMorph(
  state: MorphState,
  current: BioclastParams,
  target: Partial<BioclastParams>,
  durationSec: number,
  easing: EasingName,
): void {
  state.from = snapshotParams(current);
  // Fill in defaults for any keys the target preset omits, so we morph toward
  // a fully-specified state instead of half-blending.
  state.to = { ...DEFAULT_BIOCLAST, ...target };
  state.duration = Math.max(0.05, durationSec);
  state.easing = easing;
  state.startedAt = performance.now() / 1000;
  state.active = true;
}

export function cancelMorph(state: MorphState): void {
  state.active = false;
}

/**
 * Advance the morph one frame. Writes interpolated values directly into
 * `params`. Returns true if morph is still active, false when complete.
 */
export function tickMorph(state: MorphState, params: BioclastParams): boolean {
  if (!state.active) return false;
  const now = performance.now() / 1000;
  const t = (now - state.startedAt) / state.duration;
  const e = ease(state.easing, t);
  const from = state.from as Record<string, unknown>;
  const to = state.to as Record<string, unknown>;
  const target = params as unknown as Record<string, unknown>;
  for (const k of Object.keys(to)) {
    const a = from[k];
    const b = to[k];
    if (typeof a === "number" && typeof b === "number") {
      target[k] = a + (b - a) * e;
    } else if (typeof b === "boolean" || typeof b === "string") {
      // Snap at 50% so toggles flip cleanly mid-morph.
      target[k] = e >= 0.5 ? b : (typeof a === "boolean" || typeof a === "string" ? a : b);
    }
  }
  if (t >= 1) {
    state.active = false;
    // Final snap to target so we don't accumulate float drift.
    for (const k of Object.keys(to)) target[k] = to[k];
    return false;
  }
  return true;
}
